import { cellToLatLng } from "h3-js";
import type { GeoJSON } from "geojson";
import type {
	GeoJSONFeature,
	Map as MaplibreMap,
	MapGeoJSONFeature,
} from "maplibre-gl";

import type { SelectId, SelectLayer } from "./types";

// The geometry every map selection shares.
//
// Click selection and area selection ask the same questions of the same map —
// which id does this hit name, and is that hit inside the ring — so the answers
// live here once instead of in a copy per surface (this consolidates
// ts/lib/geo.ts, ts/map/lasso.ts and ts/map/drawModes.ts). The maths is their
// maths: ray casting on [lng, lat] rings, the same equirectangular circle
// sampling, the same ring closure.
//
// Nothing here touches Vue or a store, so the pure half runs in the node test
// environment without a map. The one map-shaped function, `featuresInRing`,
// takes the map as an argument rather than reaching for one.

/** Mean Earth radius in metres (the IUGG mean), the sphere the circle's
 * equirectangular sampling is computed on. */
const EARTH_RADIUS_M = 6371008.8;

/** Metres in one degree of latitude on that sphere. A degree of longitude is
 * the same figure scaled by the cosine of the latitude. */
const METERS_PER_DEGREE = (EARTH_RADIUS_M * Math.PI) / 180;

/** Vertices a circle ring is sampled to when the caller names none: enough that
 * a circle the size of a district parcel reads as round at the zooms the
 * workbench draws at, and few enough that the ring stays a small payload. */
const DEFAULT_CIRCLE_STEPS = 64;

/** The units a distance the operator typed can be stated in. */
type DistanceUnit = "m" | "km" | "ft" | "mi";

/** Metres in one of those units, so a typed distance becomes the metres every
 * geometry call works in. */
const METERS_PER_UNIT: Record<DistanceUnit, number> = {
	m: 1,
	km: 1000,
	ft: 0.3048,
	mi: 1609.344,
};

/**
 * Ray casting on a ring of [lng, lat] pairs. The ring may be open or closed —
 * the test treats it as cyclic, so the closing vertex a GeoJSON ring repeats is
 * a zero-length edge that never straddles — and interior rings are ignored,
 * because the areas the workbenches draw are simple ones.
 *
 * A concave ring is why this is a ray cast and not a bounding-box test: the
 * middle of a U sits inside the box and outside the shape.
 */
export function pointInRing(
	point: [number, number],
	ring: [number, number][],
): boolean {
	const [lng, lat] = point;
	let inside = false;
	for (let i = 0, j = ring.length - 1; i < ring.length; j = i++) {
		const [xi, yi] = ring[i];
		const [xj, yj] = ring[j];
		// The edge straddles the ray's latitude (so it is never horizontal, and
		// dividing by its height is safe) and crosses east of the point.
		const crosses =
			yi > lat !== yj > lat && lng < ((xj - xi) * (lat - yi)) / (yj - yi) + xi;
		if (crosses) inside = !inside;
	}
	return inside;
}

/** The outer ring of a Polygon, in [lng, lat] order. A polygon with no
 * coordinates (a source handing over a half-built shape) is an empty ring
 * rather than an error: every caller tests the length before using it. */
export function outerRing(polygon: GeoJSON.Polygon): [number, number][] {
	return (polygon.coordinates[0] ?? []) as [number, number][];
}

/** The lng/lat box of a ring: the coarse narrowing a caller does before the
 * exact ray cast. */
export function bboxOfRing(
	ring: [number, number][],
): [[number, number], [number, number]] {
	let minLng = Infinity;
	let minLat = Infinity;
	let maxLng = -Infinity;
	let maxLat = -Infinity;
	for (const [lng, lat] of ring) {
		if (lng < minLng) minLng = lng;
		if (lng > maxLng) maxLng = lng;
		if (lat < minLat) minLat = lat;
		if (lat > maxLat) maxLat = lat;
	}
	return [
		[minLng, minLat],
		[maxLng, maxLat],
	];
}

/**
 * The closed polygon for a ring of points, or null when there are fewer than
 * three of them and therefore no shape. A path that already ends where it began
 * is returned as it stands rather than closed twice, which would leave a
 * zero-length edge: terra-draw hands over a closed ring, a sampled drag is open.
 */
export function ringToPolygon(
	points: [number, number][],
): GeoJSON.Polygon | null {
	if (points.length < 3) return null;
	const ring: [number, number][] = points.map(([lng, lat]) => [lng, lat]);
	const first = ring[0];
	const last = ring[ring.length - 1];
	if (first[0] !== last[0] || first[1] !== last[1])
		ring.push([first[0], first[1]]);
	return { type: "Polygon", coordinates: [ring] };
}

/**
 * A circle approximated as a closed polygon ring: `steps` vertices around
 * `center`, each `radiusMeters` metres away (equirectangular ground distance),
 * with the first vertex repeated to close the ring exactly as GeoJSON requires.
 *
 * Metres east scale by cos(latitude), which is exact enough at the radii a
 * district draws — the error over a kilometre is a fraction of a metre — and
 * keeps the maths closed-form and dependency-free. A circle must commit as a
 * polygon because the whole downstream path (spec, zone write, PostGIS) takes
 * polygons and has no circle primitive.
 */
export function circleRing(
	center: [number, number],
	radiusMeters: number,
	steps: number = DEFAULT_CIRCLE_STEPS,
): [number, number][] {
	const [lng, lat] = center;
	const metersPerDegLat = METERS_PER_DEGREE;
	const metersPerDegLng = METERS_PER_DEGREE * Math.cos((lat * Math.PI) / 180);
	const ring: [number, number][] = [];
	for (let i = 0; i < steps; i++) {
		const angle = (2 * Math.PI * i) / steps;
		ring.push([
			lng + (radiusMeters * Math.sin(angle)) / metersPerDegLng,
			lat + (radiusMeters * Math.cos(angle)) / metersPerDegLat,
		]);
	}
	// Repeat the first vertex: a GeoJSON ring's last point is its first point.
	ring.push([ring[0][0], ring[0][1]]);
	return ring;
}

/** A distance the operator typed, in metres. */
export function distanceToMeters(value: number, unit: DistanceUnit): number {
	return value * METERS_PER_UNIT[unit];
}

/** A value read as an id, or null when it is absent or not a finite number.
 * `Number` alone would turn null and an empty string into 0, which is a real
 * id. */
function finiteId(value: unknown): number | null {
	if (value === null || value === undefined || value === "") return null;
	const id = Number(value);
	return Number.isFinite(id) ? id : null;
}

/** What a layer's own `idOf` answered, kept as it stands. A string is an id in
 * its own right here — an H3 cell index is lowercase hex — so it is passed
 * through rather than run through `finiteId`, which exists to refuse strings a
 * layer never claimed. An empty string and a non-finite number still name
 * nothing. */
function declaredId(value: SelectId | null): SelectId | null {
	if (typeof value === "string") return value === "" ? null : value;
	return Number.isFinite(value) ? value : null;
}

/**
 * The id a hit names, by the one rule both widgets use: the layer's own rule
 * when it states one, else the feature's own id, else the id its properties
 * carry. `null` when none of those names one, so a hit is skipped rather than
 * selected as `NaN`.
 *
 * The fallbacks are numeric only: an id the renderer or the properties carry as
 * a string is not trusted on its own, because a layer's id and a cell's hex
 * index look alike and only the layer can say which it means. A hex string is
 * therefore an id exactly when the layer states `idOf`.
 */
export function featureIdOf(
	layer: SelectLayer,
	properties: Record<string, unknown>,
	featureId: unknown,
): SelectId | null {
	if (layer.idOf) return declaredId(layer.idOf(properties, featureId));
	return finiteId(featureId) ?? finiteId(properties.id);
}

/**
 * The point a hit stands at, in [lng, lat].
 *
 * An H3 feature carries its cell in properties and nothing else positional, so
 * the cell's centroid is the point its footprint was drawn at; a layer with some
 * other convention supplies `pointOf`. A polygon with neither has no single
 * point to test, so it is skipped rather than guessed at — which is also what
 * makes an area selection over fill layers honest: the fill's own cells decide
 * the hit, not its bbox.
 */
function hitPoint(
	layer: SelectLayer,
	feature: GeoJSONFeature,
): [number, number] | null {
	if (layer.pointOf) return layer.pointOf(feature);
	const cell = feature.properties?.cell;
	if (cell) {
		try {
			// h3 answers [lat, lng]; every ring here is [lng, lat].
			const [lat, lng] = cellToLatLng(String(cell));
			return [lng, lat];
		} catch {
			// A cell string the H3 parser refuses names no point: skip the hit
			// rather than select something at [0, 0].
			return null;
		}
	}
	const geometry = feature.geometry;
	if (geometry?.type === "Point") {
		const [lng, lat] = geometry.coordinates as [number, number];
		return [lng, lat];
	}
	return null;
}

/**
 * The rendered hits inside `ring`, grouped by the selection kind their layer
 * declares and de-duplicated per kind. This is the exact half of an instant area
 * selection: the map query narrows to the ring's box, and only hits whose own
 * point ray-casts inside the ring survive.
 *
 * A feature whose layer is not among `layers` is not this widget's to select and
 * is skipped — which is how a query that spanned several declared layers cannot
 * leak an undeclared one in.
 *
 * The ids are carried exactly as the layer's rule named them: a numeric row id
 * stays a number, a cell index stays its hex string. Coercing one into the
 * other here would make the store's keys and the ledger's ids disagree about
 * the same object.
 */
export function filterFeaturesInRing(
	features: GeoJSONFeature[],
	ring: [number, number][],
	layers: SelectLayer[],
): Map<string, SelectId[]> {
	const byLayer = new Map(layers.map((layer) => [layer.layerId, layer]));
	const ids = new Map<string, Set<SelectId>>();
	for (const feature of features) {
		const layerId = (feature as MapGeoJSONFeature).layer?.id;
		const layer = layerId ? byLayer.get(layerId) : undefined;
		if (!layer) continue;
		const id = featureIdOf(layer, feature.properties ?? {}, feature.id);
		if (id === null) continue;
		const point = hitPoint(layer, feature);
		if (!point || !pointInRing(point, ring)) continue;
		const kindIds = ids.get(layer.kind) ?? new Set<SelectId>();
		kindIds.add(id);
		ids.set(layer.kind, kindIds);
	}
	return new Map(
		[...ids].map(([kind, kindIds]) => [kind, [...kindIds]] as const),
	);
}

/**
 * The ring's [lng, lat] vertices projected into a screen-pixel box, the form
 * `queryRenderedFeatures` takes. The box of the projected vertices contains the
 * projected shape itself (a ring's interior lies inside the hull of its
 * vertices), so a hit inside the ring cannot fall outside the query.
 */
function screenBox(
	map: MaplibreMap,
	ring: [number, number][],
): [[number, number], [number, number]] {
	let minX = Infinity;
	let minY = Infinity;
	let maxX = -Infinity;
	let maxY = -Infinity;
	for (const [lng, lat] of ring) {
		const { x, y } = map.project([lng, lat]);
		if (x < minX) minX = x;
		if (x > maxX) maxX = x;
		if (y < minY) minY = y;
		if (y > maxY) maxY = y;
	}
	return [
		[minX, minY],
		[maxX, maxY],
	];
}

/**
 * The instant area selection: the rendered hits inside `ring`, grouped by
 * selection kind. The query runs in screen pixels over the ring's projected box,
 * because `queryRenderedFeatures` answers only for what is rendered — an
 * instant selection names what the operator can see, and an escalated one (the
 * same ring posted to the resource's QUERY) is what names the rest.
 *
 * Layers the style has not added yet are skipped silently: a surface declares
 * its policy before its data has to exist.
 */
export function featuresInRing(
	map: MaplibreMap,
	ring: [number, number][],
	layers: SelectLayer[],
): Map<string, SelectId[]> {
	if (ring.length < 3) return new Map();
	const layerIds = layers
		.map((layer) => layer.layerId)
		.filter((layerId) => Boolean(map.getLayer(layerId)));
	if (layerIds.length === 0) return new Map();
	const features = map.queryRenderedFeatures(screenBox(map, ring), {
		layers: layerIds,
	});
	return filterFeaturesInRing(features, ring, layers);
}
